import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useStickyPinned({ stickyTopPx }) {
  const placeholderRef = useRef(null);
  const panelRef = useRef(null);
  const rafRef = useRef(0);

  const [pinned, setPinned] = useState(false);
  const [panelHeight, setPanelHeight] = useState(0);
  const [fixedRect, setFixedRect] = useState({ left: 0, width: 0 });
  const [panelTopPx, setPanelTopPx] = useState(stickyTopPx);

  const measure = useCallback(() => {
    const placeholderEl = placeholderRef.current;
    if (!placeholderEl) return;

    const rect = placeholderEl.getBoundingClientRect();
    const shouldPin = rect.top <= stickyTopPx;
    setPinned(shouldPin);
    setPanelTopPx(shouldPin ? stickyTopPx : rect.top);

    if (shouldPin) {
      setFixedRect({ left: rect.left, width: rect.width });
    }
  }, [stickyTopPx]);

  const scheduleMeasure = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    scheduleMeasure();

    window.addEventListener('scroll', scheduleMeasure, { passive: true });
    window.addEventListener('resize', scheduleMeasure);
    return () => {
      window.removeEventListener('scroll', scheduleMeasure);
      window.removeEventListener('resize', scheduleMeasure);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleMeasure]);

  useLayoutEffect(() => {
    const panelEl = panelRef.current;
    if (!panelEl) return;

    const update = () => setPanelHeight(panelEl.getBoundingClientRect().height);
    update();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      update();
      scheduleMeasure();
    });
    ro.observe(panelEl);
    return () => ro.disconnect();
  }, [scheduleMeasure]);

  const dynamicHeight = `calc(100vh - ${panelTopPx}px - var(--space-200))`;

  return {
    pinned,
    placeholderRef,
    panelRef,
    placeholderStyle: { height: pinned ? panelHeight : undefined },
    panelStyle: pinned
      ? {
          left: fixedRect.left,
          width: fixedRect.width,
          height: dynamicHeight,
        }
      : { height: dynamicHeight },
  };
}
